import { z } from "zod";
/**
 * Workflow status — drafts are being edited, active will run once execution
 * lands, paused is suspended. Execution is out of scope for the MVP so status
 * is currently cosmetic + list-filter only.
 */
export const WORKFLOW_STATUSES = ["draft", "active", "paused"];
export const WORKFLOW_NODE_TYPES = [
    "trigger",
    "agent",
    "condition",
    "action",
];
export const TRIGGER_SUBTYPES = [
    "manual",
    "schedule",
    "webhook",
    "channel_message",
];
export const ACTION_TYPES = [
    "http_post",
    "send_email",
    "wait_for_email_reply",
    "post_to_channel",
    // Generic: invoke any MCP tool the executing agent already has access to.
    // Config: { tool: string, args: Record<string, unknown>, awaiting?: { type, correlationFromOutput? } }
    // This is the runtime's primary action shape — the other types are
    // legacy/specialised forms kept for the existing canvas UI.
    "mcp_tool",
];
/**
 * Per-node-type data shapes. Kept deliberately loose (passthrough) so we can
 * evolve fields without migrations — the whole node array is a single jsonb
 * column. The server does shape-level validation on save but permits
 * unknown keys so the client can add fields freely.
 */
const triggerDataSchema = z
    .object({
    label: z.string().max(200).optional(),
    subtype: z.enum(TRIGGER_SUBTYPES).default("manual"),
    cronExpression: z.string().max(100).optional(),
    timezone: z.string().max(100).optional(),
    channelId: z.string().uuid().optional(),
    webhookId: z.string().max(200).optional(),
    description: z.string().max(2000).optional(),
})
    .passthrough();
const agentDataSchema = z
    .object({
    label: z.string().max(200).optional(),
    agentId: z.string().uuid().optional(),
    promptTemplate: z.string().max(10000).optional(),
    description: z.string().max(2000).optional(),
    // Per-step model override — a model_catalogue modelId (e.g.
    // "claude-opus-4-8"). Absent/empty = the agent's own configured model.
    model: z.string().max(200).optional(),
    // Backup model used when the primary fails at wake time or the step is
    // being retried. The agent's own model is always the final fallback.
    fallbackModel: z.string().max(200).optional(),
    // What happens if this node parks on a wait (e.g. a Teams card) and the
    // deadline passes with no response. "fail" (default) fails the run;
    // "continue" resumes the workflow with timed_out=true so a downstream
    // Condition can branch on the timeout. Inert on nodes that never wait.
    onAwaitTimeout: z.enum(["fail", "continue"]).optional(),
})
    .passthrough();
export const workflowConditionChoiceSchema = z.object({
    id: z.string().min(1).max(100),
    label: z.string().min(1).max(200),
    expression: z.string().max(2000).optional(),
    isDefault: z.boolean().optional(),
});
export const workflowConditionDataSchema = z
    .object({
    label: z.string().max(200).optional(),
    expression: z.string().max(2000).optional(),
    description: z.string().max(2000).optional(),
    mode: z.enum(["binary", "multiple"]).optional(),
    choices: z.array(workflowConditionChoiceSchema).min(2).max(8).optional(),
})
    .passthrough();
const actionDataSchema = z
    .object({
    label: z.string().max(200).optional(),
    actionType: z.enum(ACTION_TYPES).default("http_post"),
    description: z.string().max(2000).optional(),
    // Flat bag of per-action-type config — validated per action type by the
    // UI form, kept loose here.
    config: z.record(z.string(), z.unknown()).optional(),
})
    .passthrough();
/** A single node on the canvas. Keys match ReactFlow's Node shape. */
export const workflowNodeSchema = z.object({
    id: z.string().min(1).max(200),
    type: z.enum(WORKFLOW_NODE_TYPES),
    position: z.object({ x: z.number(), y: z.number() }),
    data: z.union([
        triggerDataSchema,
        agentDataSchema,
        workflowConditionDataSchema,
        actionDataSchema,
    ]),
});
/** A single edge on the canvas. Keys match ReactFlow's Edge shape. */
export const workflowEdgeSchema = z.object({
    id: z.string().min(1).max(200),
    source: z.string().min(1).max(200),
    target: z.string().min(1).max(200),
    // Binary conditions use "yes"/"no". Multiple-choice conditions use the
    // stable choice ID stored in node.data.choices.
    sourceHandle: z.string().min(1).max(100).nullable().optional(),
    targetHandle: z.string().nullable().optional(),
    label: z.string().max(200).optional(),
    animated: z.boolean().optional(),
});
export const createAgentWorkflowSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    // When a workflow is created from an agent's workspace, the new workflow is
    // seeded with an Agent node bound to this agent so it belongs to that agent
    // straight away (the "This agent" list keys off an agent node, not a column).
    agentId: z.string().uuid().optional(),
});
export const updateAgentWorkflowSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    status: z.enum(WORKFLOW_STATUSES).optional(),
    nodes: z.array(workflowNodeSchema).optional(),
    edges: z.array(workflowEdgeSchema).optional(),
})
    .refine((v) => Object.keys(v).length > 0, {
    message: "At least one field is required",
});
/**
 * Author a complete workflow in one shot — used by the chat-driven
 * authoring MCP tool (create_workflow). Unlike createAgentWorkflowSchema
 * (name/description only), this accepts the full graph so an agent can build
 * the whole thing from a natural-language brief. Defaults to status "draft"
 * so a human reviews it on the canvas before it goes live.
 */
export const authorWorkflowSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    status: z.enum(WORKFLOW_STATUSES).optional(),
    nodes: z.array(workflowNodeSchema).optional(),
    edges: z.array(workflowEdgeSchema).optional(),
});
//# sourceMappingURL=agent-workflow.js.map